import type { LineFulfillmentValue, OrderLineKindValue } from './order-math.js'

/**
 * Where a line is on its way to the customer, and which way it may move from
 * there — with NO runtime dependencies.
 *
 * Its own module, reachable as `@fitting-room/contracts/fulfillment`, for the
 * reason `./order-math.ts` is: the console draws one "next step" button and
 * one undo per line, and the API refuses any other move. Both have to agree
 * on what the next step IS, so both import this file rather than one of them
 * keeping a copy that drifts by a step. `docs/22-back-office.md` § "Pickup
 * and arrival".
 */

/**
 * The two ways a line reaches the customer, and the lines that don't.
 *
 * `supplier`: cut to order (or an accessory ordered in) — to order, ordered,
 * shipped, arrived, picked up. `stock`: off the rack (or an accessory from
 * stock) — on hold, picked up. `none`: the seamstress's work, and anything
 * else, which nobody takes home as a thing.
 */
export type FulfillmentPathValue = 'supplier' | 'stock' | 'none'

export const SUPPLIER_STEPS = ['to_order', 'ordered', 'shipped', 'arrived', 'picked_up'] as const
export const STOCK_STEPS = ['on_hold', 'picked_up'] as const

/** The steps a line can be moved ON to. The first step of each path is where a line starts, never where it is sent. */
export const FULFILLMENT_TARGETS = ['ordered', 'shipped', 'arrived', 'picked_up'] as const
export type FulfillmentTargetValue = (typeof FULFILLMENT_TARGETS)[number]

export interface FulfillmentLineShape {
  readonly kind: OrderLineKindValue
  readonly fulfillment: LineFulfillmentValue
  readonly arrivedAt?: string | undefined
}

/**
 * Which path this line is on.
 *
 * Read from the kind, except for an accessory, which can go either way and
 * says which by where it stands: waiting on the supplier, or held. Once
 * picked up the two look alike, and `arrivedAt` tells them apart — only a
 * line that came from the supplier ever arrived.
 */
export function fulfillmentPath(line: FulfillmentLineShape): FulfillmentPathValue {
  if (line.fulfillment === 'none') return 'none'
  switch (line.kind) {
    case 'made_to_order':
      return 'supplier'
    case 'off_the_rack':
      return 'stock'
    case 'accessory':
      if (line.fulfillment === 'on_hold') return 'stock'
      if (line.fulfillment === 'picked_up') return line.arrivedAt ? 'supplier' : 'stock'
      return 'supplier'
    case 'alteration':
    case 'other':
      return 'none'
  }
}

function stepsOf(path: FulfillmentPathValue): readonly LineFulfillmentValue[] {
  if (path === 'supplier') return SUPPLIER_STEPS
  if (path === 'stock') return STOCK_STEPS
  return []
}

/** The one step this line can be moved on to, or `null` — picked up, or no fulfillment at all. */
export function nextFulfillment(line: FulfillmentLineShape): LineFulfillmentValue | null {
  const steps = stepsOf(fulfillmentPath(line))
  const at = steps.indexOf(line.fulfillment)
  return at >= 0 && at < steps.length - 1 ? (steps[at + 1] ?? null) : null
}

/**
 * The step an undo puts this line back to, or `null` — it is where it
 * started. One step, always: undoing a pickup puts a made-to-order gown back
 * to arrived, not to wherever it was before somebody's run of clicks.
 */
export function previousFulfillment(line: FulfillmentLineShape): LineFulfillmentValue | null {
  const steps = stepsOf(fulfillmentPath(line))
  const at = steps.indexOf(line.fulfillment)
  return at > 0 ? (steps[at - 1] ?? null) : null
}

/** The ETA that counts: what staff typed, then what the supplier link said. `YYYY-MM-DD`. */
export function effectiveEta(line: { readonly manualEta?: string | undefined; readonly systemEta?: string | undefined }): string | undefined {
  return line.manualEta ?? line.systemEta
}

/* --------------------------------- alerts --------------------------------- */

/**
 * The alerts, one per row of `docs/22` § "Alerts", in that order — which is
 * also the order a tie is broken in.
 */
export const ALERT_KINDS = [
  'supplier_not_ordered',
  'no_eta',
  'eta_too_late',
  'eta_passed',
  'arrived_not_picked_up',
  'wedding_soon',
  'on_hold_too_long',
  'balance_due',
  'follow_up_due',
] as const
export type AlertKindValue = (typeof ALERT_KINDS)[number]

/**
 * `critical` threatens a wedding: a gown not ordered, due too late, late, or
 * not in the customer's hands with the day close. `warning` is housekeeping
 * the shop should get to — an ETA to chase, a gown waiting, money to take, a
 * call to make.
 */
export const ALERT_SEVERITIES = ['critical', 'warning'] as const
export type AlertSeverityValue = (typeof ALERT_SEVERITIES)[number]
